/**
 * The medal catalogue — every medal in the game, defined once.
 *
 * Imported by BOTH the client (to render) and the API (to award), so a
 * threshold can never drift between what the server grants and what the
 * cabinet claims you need. Deliberately free of DOM and three.js imports so
 * it loads in Node with no bundler involved.
 *
 * It lives under `api/` rather than `src/` for one blunt deployment reason:
 * the API image's build context is `api/` alone (see api/Dockerfile), so a
 * file in `src/` would simply not exist in the deployed container and the
 * server would fail to boot. The game's own image copies the whole repo, so
 * it can reach in here — `api/` is the only directory both builds can see.
 *
 * Thresholds are tuned generously on purpose: the database is young, and a
 * leaderboard where nobody has earned anything teaches players nothing about
 * what there is to chase. They are all in this file so they can be raised
 * once there is real play data, without touching award logic.
 *
 * `rarity` orders the three medals shown beside a leaderboard name (highest
 * first). It is a hand-set weight, not a measured population statistic —
 * roughly "how much work is this", so a maxed Campaign outranks a first-tier
 * piece kill but loses to the one-in-many Feats.
 */

/** Tier metals, in ascending order. Named here; drawn in ./render.js. */
export const METALS = ['bronze', 'iron', 'silver', 'gold', 'platinum', 'obsidian'];

/** Piece letters as `chess.js` stores them on `move.piece.type`. */
const PIECE_NAMES = {
  p: 'Pawn',
  n: 'Knight',
  b: 'Bishop',
  r: 'Rook',
  q: 'Queen',
  k: 'King'
};

/* Kills-per-piece thresholds. The king's are an order of magnitude lower
   because a king capturing at all is unusual — it only happens when a piece
   is left hanging next to him, so 25 king kills is a career, not an evening. */
const KILL_TIERS = {
  p: [10, 30, 60],
  n: [10, 30, 60],
  b: [10, 30, 60],
  r: [10, 30, 60],
  q: [10, 30, 60],
  k: [3, 10, 25]
};

const KILL_RARITY = { p: 10, n: 12, b: 12, r: 12, q: 16, k: 30 };

/** One Butcher's Bill medal per piece type. */
const butchersBill = Object.entries(KILL_TIERS).map(([type, thresholds]) => ({
  id: `kills_${type}`,
  family: 'butchers-bill',
  name: {
    p: 'Levy',
    n: 'Cavalier',
    b: 'Zealot',
    r: 'Siegebreaker',
    q: 'Executioner',
    k: 'Warrior King'
  }[type],
  description: `Captures taken with the ${PIECE_NAMES[type].toLowerCase()}.`,
  glyph: type,
  /* Which piece's kills this counts. Separate from `glyph` deliberately:
     Peasant's Mate also draws a pawn but counts nothing of the sort. */
  piece: type,
  rarity: KILL_RARITY[type],
  /* Tier metals start at bronze and skip to gold for the third, so a
     three-tier medal and a six-tier one still read as "more metal is
     better" side by side. */
  /* Label stays short — which piece it counts is already the medal's own
     name and description, and repeating it here wrapped the cabinet's tier
     note onto three lines. */
  tiers: thresholds.map((at, i) => ({
    at,
    metal: ['bronze', 'silver', 'gold'][i],
    label: `${at} kills`
  }))
}));

export const MEDALS = [
  {
    id: 'campaign',
    family: 'campaign',
    name: 'Campaign',
    description: 'Games won, all modes counted.',
    glyph: 'swords',
    rarity: 20,
    tiers: [
      { at: 5, metal: 'bronze', label: 'Recruit' },
      { at: 10, metal: 'iron', label: 'Soldier' },
      { at: 20, metal: 'silver', label: 'Veteran' },
      { at: 40, metal: 'gold', label: 'Champion' },
      { at: 75, metal: 'platinum', label: 'Warlord' },
      { at: 150, metal: 'obsidian', label: 'Immortal' }
    ]
  },

  ...butchersBill,

  /* Feats are binary: earned once, no tiers, and every one of them is
     decided by a single game's move list. */
  {
    id: 'feat_ghost',
    family: 'feats',
    name: 'Ghost Kill',
    description: 'Win a game in which you took a pawn en passant.',
    glyph: 'ghost',
    rarity: 40,
    tiers: [{ at: 1, metal: 'silver', label: 'Earned' }]
  },
  {
    id: 'feat_flawless',
    family: 'feats',
    name: 'Flawless',
    description: 'Win without losing a single piece.',
    glyph: 'shield',
    rarity: 70,
    tiers: [{ at: 1, metal: 'platinum', label: 'Earned' }]
  },
  {
    id: 'feat_blitz',
    family: 'feats',
    name: 'Lightning War',
    description: 'Win in under 20 moves.',
    glyph: 'bolt',
    rarity: 55,
    tiers: [{ at: 1, metal: 'gold', label: 'Earned' }]
  },
  {
    id: 'feat_comeback',
    family: 'feats',
    name: 'Headless',
    description: 'Win a game after losing your queen.',
    glyph: 'broken-crown',
    rarity: 45,
    tiers: [{ at: 1, metal: 'gold', label: 'Earned' }]
  },
  {
    id: 'feat_peasant',
    family: 'feats',
    name: "Peasant's Mate",
    description: 'Deliver checkmate with a pawn.',
    glyph: 'p',
    rarity: 80,
    tiers: [{ at: 1, metal: 'obsidian', label: 'Earned' }]
  },

  /* Both Dethroned medals need the rank tracking in `leaderboard_reign`;
     neither can be derived from a move list alone. */
  {
    id: 'regicide',
    family: 'dethroned',
    name: 'Regicide',
    description: 'Beat the player who held first place when the match began.',
    glyph: 'crossed-crown',
    rarity: 85,
    tiers: [{ at: 1, metal: 'platinum', label: 'Earned' }]
  },
  {
    id: 'usurper',
    family: 'dethroned',
    name: 'Usurper',
    description: 'Take first place from the player who held it.',
    glyph: 'crown',
    rarity: 95,
    tiers: [{ at: 1, metal: 'obsidian', label: 'Earned' }]
  }
];

export const FAMILIES = [
  { id: 'campaign', name: 'Campaign', blurb: 'The long war, counted in victories.' },
  { id: 'butchers-bill', name: "Butcher's Bill", blurb: 'Blood on each piece, tallied separately.' },
  { id: 'feats', name: 'Feats of Arms', blurb: 'Single games that earned a story.' },
  { id: 'dethroned', name: 'Dethroned', blurb: 'Reserved for those who unseat a champion.' }
];

const BY_ID = new Map(MEDALS.map((m) => [m.id, m]));

export const medalById = (id) => BY_ID.get(id) ?? null;

/**
 * The highest tier index a given count qualifies for, or -1 for none.
 * Tiers are ascending, so this walks from the top and takes the first hit.
 */
export function tierFor(medal, count) {
  for (let i = medal.tiers.length - 1; i >= 0; i--) {
    if (count >= medal.tiers[i].at) return i;
  }
  return -1;
}

/**
 * Picks the three medals to show beside a leaderboard name: rarest first,
 * and for equal rarity the higher tier wins, so a maxed medal outranks the
 * same medal at tier one.
 */
export function topMedals(earned, limit = 3) {
  return [...earned]
    .sort((a, b) => {
      const ra = medalById(a.medal_id)?.rarity ?? 0;
      const rb = medalById(b.medal_id)?.rarity ?? 0;
      return rb - ra || b.tier - a.tier;
    })
    .slice(0, limit);
}
